import * as React from 'react';
import {RangeType} from "rsuite/DateRangePicker";
import moment from "moment/moment";
import {DateRangePicker} from "rsuite";

export type DateRanges = 'Today' | 'Yesterday' | '2 Days Ago' | 'Last 7 Days' | 'Month to Date' | 'Month to Yesterday' | 'Last 30 Days' | 'Last Month' | '7 to 45 Days' | 'Last 60 Days' | 'Last 6 Month' | 'Last 12 Month' | 'Last 24 Month' | 'Year to Date'

const RANGE_BUILDERS: Record<string, () => [Date, Date]> = {
    'Today': () => [moment().startOf("day").toDate(), moment().endOf("day").toDate()],
    'Yesterday': () => [moment().subtract(1, "day").startOf("day").toDate(), moment().subtract(1, "day").endOf("day").toDate()],
    '2 Days Ago': () => [moment().subtract(2, "day").startOf("day").toDate(), moment().subtract(2, "day").endOf("day").toDate()],
    'Last 7 Days': () => [moment().subtract(7, "days").startOf("day").toDate(), moment().endOf("day").toDate()],
    'Month to Date': () => [moment().startOf("month").toDate(), moment().endOf("day").toDate()],
    'Month to Yesterday': () => [moment().startOf("month").toDate(), moment().subtract(1, "day").endOf("day").toDate()],
    'Last 30 Days': () => [moment().subtract(30, "days").startOf("day").toDate(), moment().endOf("day").toDate()],
    'Last Month': () => [moment().subtract(1, "month").startOf("month").toDate(), moment().subtract(1, "month").endOf("month").toDate()],
    '7 to 45 Days': () => [moment().subtract(45, "days").startOf("day").toDate(), moment().endOf("day").subtract(8, "days").toDate()],
    'Last 60 Days': () => [moment().subtract(60, "days").startOf("day").toDate(), moment().endOf("day").toDate()],
    'Last 6 Month': () => [moment().subtract(6, "month").startOf("month").toDate(), moment().subtract(1, "month").endOf("month").toDate()],
    'Last 12 Month': () => [moment().subtract(12, "month").startOf("month").toDate(), moment().subtract(1, "month").endOf("month").toDate()],
    'Last 24 Month': () => [moment().subtract(24, "month").startOf("month").toDate(), moment().subtract(1, "month").endOf("month").toDate()],
    'Year to Date': () => [moment().startOf("year").toDate(), moment().endOf("day").toDate()],
};

const rangePlacement = () => (window.innerWidth < 768 ? 'bottom' : 'left') as 'bottom' | 'left';

export const buildPredefinedRanges = (): RangeType[] =>
    Object.keys(RANGE_BUILDERS).map((label) => ({
        label,
        value: RANGE_BUILDERS[label](),
        placement: rangePlacement(),
    }));

const predefinedRanges: RangeType[] = buildPredefinedRanges();

/**
 * Calendar / picker commits always use start-of-day → end-of-day.
 * Backstop for commits that bypass the calendar (typed input, onChange/onOk firing
 * twice), so a mid-day clock can never reach a consumer's query.
 */
export const normalizeRange = (value: [Date, Date]): [Date, Date] => {
    const next: [Date, Date] = [new Date(value[0].valueOf()), new Date(value[1].valueOf())];
    next[0].setHours(0, 0, 0, 0);
    next[1].setHours(23, 59, 59, 0);
    return next;
};

type DateRangeProps = {
    showTime?: boolean;
    dateRange: any;
    setDateRange: any;
    style: any;
    placement?: 'bottomStart' | 'bottomEnd';
};

function validRange(value: any): [Date, Date] | null {
    if (!Array.isArray(value) || !(value[0] instanceof Date) || !(value[1] instanceof Date)) return null;
    if (isNaN(value[0].getTime()) || isNaN(value[1].getTime())) return null;
    return [value[0], value[1]];
}

function rangeTime(value: any, index: number): number | null {
    const date = value?.[index];
    return date instanceof Date && !isNaN(date.getTime()) ? date.getTime() : null;
}

function DateRangePickerControl({showTime = true, dateRange, setDateRange, style, placement}: DateRangeProps) {
    const focusCount = React.useRef(0);
    const [open, setOpen] = React.useState(false);
    const [draft, setDraft] = React.useState<[Date, Date] | null>(null);
    const ranges = React.useMemo(() => buildPredefinedRanges(), []);

    // The calendar stamps its own clock onto clicked days; anchor it to SOD/EOD so the
    // panel never shows (or commits) the time the picker happened to be opened.
    const calendarBounds = React.useMemo<[Date, Date]>(() => {
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        const end = new Date();
        end.setHours(23, 59, 59, 0);
        return [start, end];
    }, []);

    const committed = validRange(dateRange);
    // While the panel is open, keep the in-progress value local so a parent refresh
    // cannot hand rsuite a half-selected date.
    const pickerValue = open ? (draft ?? committed) : committed;

    const applyRange = React.useCallback((value: any) => {
        if (!value?.[0] || !value?.[1]) return;
        const start = value[0] instanceof Date ? value[0] : new Date(value[0]);
        const end = value[1] instanceof Date ? value[1] : new Date(value[1]);
        if (isNaN(start.getTime()) || isNaN(end.getTime())) return;
        const next = normalizeRange([start, end]);
        setDraft(next);
        setDateRange(next);
    }, [setDateRange]);

    return (
            <DateRangePicker
                format={showTime ? "MM-dd-yyyy HH:mm:ss" : "MM-dd-yyyy"}
                value={pickerValue}
                defaultCalendarValue={calendarBounds}
                showOneCalendar={window.innerWidth < 768}
                ranges={ranges}
                onChange={applyRange}
                onOk={applyRange}
                cleanable={false}
                preventOverflow={false}
                style={style}
                placement={window.innerWidth < 768 ? 'bottomStart' : (placement || 'bottomStart')}
                onOpen={() => {
                    setDraft(committed);
                    setOpen(true);
                }}
                onClose={() => {
                    focusCount.current = 0;
                    setOpen(false);
                }}
                onFocus={(e: any) => {
                    focusCount.current += 1;
                    if (focusCount.current === 1) e.target.blur();
                }}
            />
    )
}

class DateRangeBoundary extends React.Component<DateRangeProps, {failed: boolean; key: number}> {
    state = {failed: false, key: 0};

    static getDerivedStateFromError() {
        return {failed: true};
    }

    render() {
        if (this.state.failed) {
            return (
                <button type="button" onClick={() => this.setState({failed: false, key: this.state.key + 1})}>
                    Reset date range
                </button>
            );
        }
        return <DateRangePickerControl key={this.state.key} {...this.props} />;
    }
}

export default React.memo(DateRangeBoundary, (prev, next) => (
    prev.showTime === next.showTime
    && prev.placement === next.placement
    && rangeTime(prev.dateRange, 0) === rangeTime(next.dateRange, 0)
    && rangeTime(prev.dateRange, 1) === rangeTime(next.dateRange, 1)
));

export const RangeToTimes = (defaultRange: DateRanges) => {
    return RANGE_BUILDERS[defaultRange]?.() ?? predefinedRanges.filter((range: RangeType) => range.label === defaultRange)[0]?.value;
}

export const getDateRangeByLabel = (label: string): Date[] | null => {
    const build = RANGE_BUILDERS[label];
    if (build) return build();
    const match = predefinedRanges.find((range) => range.label === label);
    const value = match?.value;
    if (!value || typeof value === 'function' || !Array.isArray(value)) return null;
    return [value[0], value[1]];
}

const sameCalendarDay = (a: Date | moment.Moment, b: Date | moment.Moment) =>
    moment(a).format("YYYY-MM-DD") === moment(b).format("YYYY-MM-DD");

/** Which predefined range label matches the current picker value (calendar days), if any. */
export function matchDateRangePreset(dateRange: any): DateRanges | null {
    if (!dateRange?.[0] || !dateRange?.[1]) return null;
    for (const label of Object.keys(RANGE_BUILDERS) as DateRanges[]) {
        const built = RANGE_BUILDERS[label]();
        if (sameCalendarDay(dateRange[0], built[0]) && sameCalendarDay(dateRange[1], built[1])) {
            return label;
        }
    }
    return null;
}

/**
 * Prior period of equal shape for a matched preset.
 * Open-ended "to now" windows (Today, Last N Days, MTD, YTD, …) use the same clock time on the prior end.
 */
export function getPresetCompareRange(label: DateRanges, now: moment.Moment = moment()): [Date, Date] | null {
    switch (label) {
        case "Today":
            return [
                now.clone().subtract(1, "day").startOf("day").toDate(),
                now.clone().subtract(1, "day").toDate(),
            ];
        case "Yesterday":
            return RangeToTimes("2 Days Ago") as [Date, Date];
        case "2 Days Ago":
            return [
                now.clone().subtract(3, "day").startOf("day").toDate(),
                now.clone().subtract(3, "day").endOf("day").toDate(),
            ];
        case "Last 7 Days":
        case "Last 30 Days":
        case "Last 60 Days":
        case "7 to 45 Days": {
            const cur = RangeToTimes(label) as [Date, Date];
            const ms = moment(cur[1]).diff(moment(cur[0]));
            const compareEnd = moment(cur[0]).subtract(1, "second");
            return [compareEnd.clone().subtract(ms, "milliseconds").toDate(), compareEnd.toDate()];
        }
        case "Month to Date": {
            const day = Math.min(now.date(), now.clone().subtract(1, "month").daysInMonth());
            return [
                now.clone().subtract(1, "month").startOf("month").toDate(),
                now.clone().subtract(1, "month").date(day).hour(now.hour()).minute(now.minute()).second(now.second()).millisecond(0).toDate(),
            ];
        }
        case "Month to Yesterday": {
            const y = now.clone().subtract(1, "day");
            const day = Math.min(y.date(), y.clone().subtract(1, "month").daysInMonth());
            return [
                y.clone().subtract(1, "month").startOf("month").toDate(),
                y.clone().subtract(1, "month").date(day).endOf("day").toDate(),
            ];
        }
        case "Last Month":
            return [
                now.clone().subtract(2, "month").startOf("month").toDate(),
                now.clone().subtract(2, "month").endOf("month").toDate(),
            ];
        case "Last 6 Month":
            return [
                now.clone().subtract(12, "month").startOf("month").toDate(),
                now.clone().subtract(7, "month").endOf("month").toDate(),
            ];
        case "Last 12 Month":
            return [
                now.clone().subtract(24, "month").startOf("month").toDate(),
                now.clone().subtract(13, "month").endOf("month").toDate(),
            ];
        case "Last 24 Month":
            return [
                now.clone().subtract(48, "month").startOf("month").toDate(),
                now.clone().subtract(25, "month").endOf("month").toDate(),
            ];
        case "Year to Date": {
            const lastYear = now.clone().subtract(1, "year");
            return [
                lastYear.clone().startOf("year").toDate(),
                lastYear.clone().hour(now.hour()).minute(now.minute()).second(now.second()).millisecond(0).toDate(),
            ];
        }
        default:
            return null;
    }
}

/** Compare window for the current picker value when it matches a predefined preset; otherwise null. */
export function getCompareRangeForDateRange(dateRange: any): [Date, Date] | null {
    const label = matchDateRangePreset(dateRange);
    if (!label) return null;
    return getPresetCompareRange(label);
}
